import {
  BankOutlined,
  CalendarOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { blue } from '../../color'
import { AvatarHasName } from '../../components/Common/Avatar'
import SelectPeoplePopup from '../../components/SelectPeoplePopup/SelectPeoplePopup'
import { ASSIGNEE_TYPE } from '../../constants'
import { KpiV2Request } from '../../requests/KpiV2Request'
import {
  SelectList,
  SelectTitle,
} from '../Documents/InternalDocumentIncomingDocCreatePage/InternalDocumentIncomingDocCreatePageStyled'

const { TextArea } = Input
const { Option } = Select

const ROLE_SECTIONS = [{ key: ASSIGNEE_TYPE.HANDLER, label: 'Người thực hiện' }]

const LABEL_COL = { flex: '0 0 200px' }
const WRAPPER_COL = { flex: 1 }
const FORM_ITEM_STYLE = { marginBottom: 14 }

const KpiV2SubUpsertModal = ({
  visible,
  onClose,
  onSuccess,
  aspects = [],
  parentKpi = null,
  initialData = null,
  defaultAspectId = null,
  selectPeopleStore,
  authenticationStore,
}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const isUpdate = !!initialData

  const {
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
  } = selectPeopleStore

  useEffect(() => {
    if (!visible) return
    selectPeopleStore.clearSelectPeopleStore()
    selectPeopleStore.companyCode = authenticationStore.currentCompanyCode()
    selectPeopleStore.getDepartmentWithUsers()

    if (isUpdate && initialData) {
      form.setFieldsValue({
        ...initialData,
        start_date: initialData.start_date
          ? moment(initialData.start_date)
          : null,
        end_date: initialData.end_date ? moment(initialData.end_date) : null,
        report_date_range: initialData.report_date
          ? [
              moment(initialData.report_date),
              initialData.report_end_date
                ? moment(initialData.report_end_date)
                : null,
            ]
          : null,
      })
      if (initialData.assignees) {
        const newUserData = {}
        const newDeptData = {}
        const newGroupData = {}
        ROLE_SECTIONS.forEach(({ key }) => {
          const members = initialData.assignees
            .filter(a => a.permission === key)
            .map(a => ({
              id: a.assignee_code,
              name: a.name_uppercase,
              full_name: a.name_uppercase,
              image_id: a.image,
              assignee_type: a.assignee_type,
            }))
          newUserData[key] = members.filter(
            a => a.assignee_type === ASSIGNEE_TYPE.USER
          )
          newDeptData[key] = members.filter(
            a => a.assignee_type === ASSIGNEE_TYPE.DEPARTMENT
          )
          newGroupData[key] = members.filter(
            a => a.assignee_type === ASSIGNEE_TYPE.GROUP
          )
        })
        selectPeopleStore.setSelectUserData(newUserData)
        selectPeopleStore.setSelectDepartmentData(newDeptData)
        selectPeopleStore.setSelectGroupData(newGroupData)
      }
    } else if (parentKpi) {
      form.setFieldsValue({
        aspect_id: parentKpi.aspect_id,
        purpose: parentKpi.purpose,
        kgi: parentKpi.kgi,
        csf: parentKpi.csf,
        kpi: parentKpi.kpi,
        measurement: parentKpi.measurement,
        indicator_weight: parentKpi.indicator_weight,
        evaluation_rule: parentKpi.evaluation_rule,
        start_date: parentKpi.start_date ? moment(parentKpi.start_date) : null,
        end_date: parentKpi.end_date ? moment(parentKpi.end_date) : null,
      })
    } else if (defaultAspectId) {
      form.setFieldsValue({ aspect_id: defaultAspectId })
    } else {
      form.resetFields()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  const getListByKey = key => [
    ...(selectUserData[key] || []),
    ...(selectDepartmentData[key] || []),
    ...(selectGroupData[key] || []),
  ]

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      const assignees = ROLE_SECTIONS.flatMap(({ key }) =>
        getListByKey(key).map(el => ({
          assignee_code: el.id,
          assignee_type: el.assignee_type || ASSIGNEE_TYPE.USER,
          permission: key,
        }))
      )

      const reportRange = values.report_date_range
      const payload = {
        ...values,
        report_date_range: undefined,
        start_date: values.start_date
          ? values.start_date.format('YYYY-MM-DD')
          : undefined,
        end_date: values.end_date
          ? values.end_date.format('YYYY-MM-DD')
          : undefined,
        report_date: reportRange?.[0]
          ? reportRange[0].format('YYYY-MM-DD')
          : undefined,
        report_end_date: reportRange?.[1]
          ? reportRange[1].format('YYYY-MM-DD')
          : undefined,
        assignees,
        attachment: [],
      }
      if (isUpdate) {
        payload.kpi_id = initialData.kpi_id
      } else if (parentKpi?.id) {
        payload.kpi_id = parentKpi.id
      }

      if (isUpdate) {
        await KpiV2Request.updateSubKpi(initialData.id, payload)
        message.success('Cập nhật KPI phụ thành công!')
      } else {
        await KpiV2Request.createSubKpi(payload)
        message.success('Tạo KPI phụ thành công!')
      }

      onSuccess?.()
      onClose()
    } catch (error) {
      if (error?.errorFields) return
      message.error(
        error?.response?.data?.message || 'Đã xảy ra lỗi, vui lòng thử lại.'
      )
    } finally {
      setLoading(false)
    }
  }

  const renderUserTagList = list =>
    list.map(el => (
      <Tag
        key={el.id}
        className="tag-selected"
        color="blue"
        closable
        onClose={() => selectPeopleStore.handleRemoveSelect(el)}>
        <AvatarHasName
          imgId={el.image_id}
          size={22}
          name={el.full_name || el.name}
          icon={
            (el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT && (
              <BankOutlined />
            )) ||
            (el.assignee_type === ASSIGNEE_TYPE.GROUP && <TeamOutlined />) || (
              <UserOutlined />
            )
          }
        />
      </Tag>
    ))

  return (
    <Modal
      title={isUpdate ? 'Chỉnh sửa KPI phụ' : 'Tạo KPI phụ'}
      visible={visible}
      onCancel={onClose}
      width={820}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Hủy bỏ</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            {isUpdate ? 'Cập nhật' : 'Tạo mới'}
          </Button>
        </div>
      }>
      <Form
        form={form}
        layout="horizontal"
        labelCol={LABEL_COL}
        wrapperCol={WRAPPER_COL}
        labelAlign="left"
        colon={false}>
        <Form.Item name="aspect_id" label="Khía cạnh:" style={FORM_ITEM_STYLE}>
          <Select placeholder="Chọn khía cạnh" allowClear>
            {aspects.map(asp => (
              <Option key={asp.id} value={asp.id}>
                {asp.context}
                {asp.weight != null ? ` (${asp.weight}%)` : ''}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="purpose" label="Mục tiêu:" style={FORM_ITEM_STYLE}>
          <Input placeholder="Nhập nội dung" />
        </Form.Item>

        <Form.Item
          name="kgi"
          label="KGI (Chỉ số mục tiêu):"
          style={FORM_ITEM_STYLE}>
          <Input placeholder="Nhập nội dung" />
        </Form.Item>

        <Form.Item
          name="csf"
          label="CSF (Hành động cốt lõi):"
          style={FORM_ITEM_STYLE}>
          <Input placeholder="Nhập nội dung" />
        </Form.Item>

        <Form.Item
          name="kpi"
          label="KPI (Chỉ tiêu đo lường):"
          style={FORM_ITEM_STYLE}>
          <Input placeholder="Nhập nội dung" />
        </Form.Item>

        <Form.Item
          name="measurement"
          label="Đơn vị tính:"
          style={FORM_ITEM_STYLE}>
          <Input placeholder="VD: %, VNĐ..." />
        </Form.Item>

        <Form.Item
          name="indicator_weight"
          label="Trọng số chỉ tiêu:"
          style={FORM_ITEM_STYLE}>
          <InputNumber
            min={0}
            max={100}
            placeholder="0-100"
            addonAfter="%"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="evaluation_rule"
          label="Quy ước đánh giá:"
          style={FORM_ITEM_STYLE}>
          <TextArea rows={3} placeholder="Nhập quy ước đánh giá..." />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="start_date"
              label="Ngày bắt đầu:"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              style={FORM_ITEM_STYLE}>
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                suffixIcon={<CalendarOutlined />}
                placeholder="dd/mm/yyyy"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="end_date"
              label="Hạn hoàn thành:"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              style={FORM_ITEM_STYLE}>
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                suffixIcon={<CalendarOutlined />}
                placeholder="dd/mm/yyyy"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="report_date_range"
          label="Thời gian báo cáo:"
          style={FORM_ITEM_STYLE}>
          <DatePicker.RangePicker
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            suffixIcon={<CalendarOutlined />}
            placeholder={['Từ ngày', 'Đến ngày']}
          />
        </Form.Item>

        <Row type="flex" gutter={30}>
          {ROLE_SECTIONS.map(({ key, label }) => (
            <Col key={key} md={24} lg={8}>
              <SelectTitle>
                <span style={{ marginRight: 10 }}>{label}:</span>
                <Space>
                  <Tooltip title={`Chọn ${label.toLowerCase()}`} color={blue}>
                    <Button
                      icon={<UserOutlined style={{ color: blue }} />}
                      onClick={() => handleOpenSelectUserDepartmentPopup(key)}
                    />
                  </Tooltip>
                </Space>
              </SelectTitle>
              <SelectList>{renderUserTagList(getListByKey(key))}</SelectList>
            </Col>
          ))}
        </Row>
      </Form>
      <SelectPeoplePopup
        onlyOneUser={
          selectPeopleStore.assigneePopupType === ASSIGNEE_TYPE.HANDLER
        }
        handleCancelSelectUser={handleCancelSelectUserDepartment}
        handleSubmitSelectUser={() =>
          setIsVisibleSelectUserDepartmentPopup(false)
        }
        isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
      />
    </Modal>
  )
}

export default inject(
  'selectPeopleStore',
  'authenticationStore'
)(observer(KpiV2SubUpsertModal))
